<template>
  <div class="invoice-dashboard-container" v-if="suppliers">
    <div class="d-flex justify-content-end mb-3">
      <div class="view-toggle-buttons">
        <button 
          class="circle-btn" 
          :class="{ 'active-view': isGridView }" 
          @click="toggleView('grid')"
          title="Affichage Grille"
        >
          <img src="@/assets/icons/grid.svg" height="16" />
        </button>
        <button 
          class="circle-btn" 
          :class="{ 'active-view': !isGridView }" 
          @click="toggleView('list')"
          title="Affichage Liste"
        >
          <img src="@/assets/icons/list.svg" height="16" />
        </button>
      </div>
    </div>

    <LoadingState v-if="loading" class="custom-card" message="Chargement des fournisseurs..." />

    <EmptyList
      v-else-if="suppliers.length === 0"
      icon="fas fa-building"
      title="Aucun fournisseur trouvé"
      description="Essayez un autre filtre."
    />

    <div v-else>
      <!-- --- MODE CARTES (GRID) --- -->
      <div v-if="isGridView" class="supplier-grid-view">
        <div 
          v-for="supplier in suppliers" 
          :key="supplier.id" 
          class="supplier-profile-card h-100 shadow-sm p-3" 
          @click="onSelectedRow(supplier)"
        >
          <div class="supplier-card-main">
            <div class="supplier-avatar-large">
              {{ supplier.companyName ? supplier.companyName.charAt(0).toUpperCase() : '?' }}
            </div>

            <div class="supplier-card-copy">
              <h5 class="company-name" :title="supplier.companyName || '-'">
                {{ supplier.companyName || '-' }}
              </h5>
              <span class="tax-badge">MF: {{ supplier.taxNumber || '-' }}</span>
            </div>

            <b-badge
              pill
              variant="light"
              class="user-count-badge"
              @click.stop="onSelectedUsersSupplier(supplier)"
            >
              <i class="fas fa-users me-1"></i> {{ supplier.usersCount || 0 }}
            </b-badge>
          </div>

          <div class="supplier-details">
            <div class="detail-item">
              <i class="fas fa-phone-alt"></i>
              <span>{{ supplier.phone || '-' }}</span>
            </div>
            <div class="detail-item">
              <i class="fas fa-envelope"></i>
              <span class="text-truncate">{{ supplier.email || '-' }}</span>
            </div>
            <div class="detail-item">
              <i class="fas fa-map-marker-alt"></i>
              <span class="text-truncate">{{ supplier.address || '-' }}</span>
            </div>
            <div class="detail-item" v-if="withCreationDate">
              <i class="far fa-calendar-alt"></i>
              <span>{{ supplier.creationDate || '-' }}</span>
            </div>
          </div>

          <div v-if="withActions" class="supplier-card-actions">
            <button class="circle-btn sm" @click.stop="onSelectedRow(supplier)">
              <img src="@/assets/icons/edit.svg" height="14" />
            </button>
            <div class="delete-btn-wrapper sm" @click.stop>
              <ConfirmAction @confirm="handleDelete(supplier.id)" />
            </div>
          </div>
        </div>
      </div>

      <!-- --- MODE TABLEAU --- -->
      <div v-else class="table-responsive custom-card shadow-sm">
        <b-table
          :items="suppliers"
          :fields="fields"
          hover
          borderless
          class="custom-table mb-0"
          :busy="loading"
          selectable
          select-mode="single"
          @row-selected="onSelectedRows"
          show-empty
          responsive
        >
          <template #table-busy>
            <LoadingState message="Chargement des fournisseurs..." />
          </template>

          <template #empty>
            <EmptyList
              class="border-0"
              icon="fas fa-building"
              title="Aucun fournisseur trouvé"
              description="Essayez un autre filtre."
            />
          </template>

          <!-- Colonne Date de création -->
          <template #cell(creationDate)="data" v-if="withCreationDate">
            <div class="date-cell">
              <i class="far fa-calendar-alt me-2 text-muted"></i>
              <span>{{ data.value || '-' }}</span>
            </div>
          </template>

          <!-- Colonne Nom + MF -->
          <template #cell(companyName)="data">
            <div class="customer-info text-start">
              <div class="avatar-mini">
                {{ data.item.companyName ? data.item.companyName.charAt(0).toUpperCase() : '?' }}
              </div>
              <div>
                <div class="font-weight-600 color-dark">{{ data.item.companyName }}</div>
                <div class="customer-tax">MF: {{ data.item.taxNumber || '-' }}</div>
              </div>
            </div>
          </template>

          <!-- Colonne Contact (Phone / Fax) -->
          <template #cell(phone)="data">
            <div class="contact-stack">
              <div class="main-contact">{{ data.item.phone || '-' }}</div>
              <div v-if="data.item.fax" class="sub-contact">Fax: {{ data.item.fax }}</div>
            </div>
          </template>

          <!-- Colonne Email -->
          <template #cell(email)="data">
            <span class="email-text">{{ data.value || '-' }}</span>
          </template>

          <!-- Colonne Utilisateurs -->
          <template #cell(usersCount)="data">
            <div class="users-interactive" @click.stop="onSelectedUsersSupplier(data.item)">
              <span class="badge bg-primary-subtle text-primary px-2 py-1">
                <i class="fas fa-user-friends me-1"></i>
                {{ data.item.usersCount || 0 }} Utilisateur(s)
              </span>
            </div>
          </template>

          <!-- Colonne Actions -->
          <template #cell(actions)="data" v-if="withActions">
            <div class="action-group">
              <button class="circle-btn" @click.stop="onSelectedRow(data.item)">
                <img src="@/assets/icons/edit.svg" height="16" />
              </button>
              <div class="delete-btn-wrapper" @click.stop>
                <ConfirmAction @confirm="handleDelete(data.item.id)" />
              </div>
            </div>
          </template>
        </b-table>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-wrapper mt-4">
      <pagination-controls
        :page="page"
        :page-size="pageSize"
        :total-items="total"
        @update:page="onPageChange($event)"
        @update:pageSize="onPageSizeChange($event)"
      />
    </div>
  </div>
</template>

<script>
import PaginationControls from "@/components/common/customPagination.vue";
import LoadingState from "@/components/common/LoadingState.vue";
import EmptyList from "@/components/common/EmptyList.vue";
import ConfirmAction from "@/components/common/customConfirmAction.vue";

export default {
  components: { LoadingState, EmptyList, PaginationControls, ConfirmAction },
  props: {
    suppliers: { type: Array, required: true },
    loading: { type: Boolean, required: true },
    withActions: { type: Boolean, default: true },
    withCreationDate: { type: Boolean, default: true },
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 10 },
    total: { type: Number, default: 1 },
    cardStyle: { type: Boolean, default: false }, // Renamed from cardStyle to be managed internally
  },
  data() {
    return {
      isGridView: this.cardStyle,
    };
  },
  methods: {
    onPageChange(p) { this.$emit('update:page', p); },
    onPageSizeChange(s) { this.$emit('update:pageSize', s); },
    onSelectedRow(row) { this.$emit('update:row', row); },
    handleDelete(id) { this.$emit('deleteById', id); },
    onSelectedUsersSupplier(supplier) { this.$emit('usersSupplier', supplier); },
    onSelectedRows(rows) { if (rows.length > 0) this.onSelectedRow(rows[0]); },
    toggleView(view) {
      this.isGridView = view === 'grid';
    },
    checkScreenSize() {
      if (window.innerWidth <= 768) {
        this.isGridView = true;
      } else {
        this.isGridView = this.cardStyle;
      }
    }
  },
  computed: {
    fields() {
      const baseFields = [
        { key: 'companyName', label: 'NOM / MATRICULE', thClass: 'th-custom', tdClass: 'td-custom' },
        { key: 'phone', label: 'CONTACT', thClass: 'th-custom', tdClass: 'td-custom' },
        { key: 'email', label: 'EMAIL', thClass: 'th-custom', tdClass: 'td-custom' },
        { key: 'usersCount', label: 'ACCÈS', thClass: 'th-custom text-center', tdClass: 'td-custom text-center' }
      ];
      if (this.withCreationDate) {
        baseFields.unshift({ key: 'creationDate', label: 'CRÉÉ LE', thClass: 'th-custom', tdClass: 'td-custom' });
      }
      if (this.withActions) {
        baseFields.push({ key: 'actions', label: '', thClass: 'th-custom text-right', tdClass: 'td-custom text-right' });
      }
      return baseFields;
    }
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
};
</script>

<style scoped>
/* Layout Base */
.invoice-dashboard-container {
  padding: 15px;
}

/* Table Style */
.custom-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e9f2;
  overflow: hidden;
}

:deep(.th-custom) {
  background-color: #fcfcfd !important;
  color: #8094ae !important;
  font-size: 11px !important;
  font-weight: 700;
  letter-spacing: 0.8px;
  padding: 16px !important;
  text-transform: uppercase;
  border-bottom: 1px solid #e5e9f2 !important;
}

:deep(.td-custom) {
  vertical-align: middle !important;
  padding: 14px 16px !important;
  color: #364a63;
  font-size: 13.5px;
  border-bottom: 1px solid #f0f2f5;
}

/* Card Style */
.supplier-profile-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e9f2;
  transition: all 0.3s ease;
  cursor: pointer;
}
.supplier-profile-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05) !important;
  border-color: #4f46e5;
}

/* Contact Stacking */
.contact-stack { display: flex; flex-direction: column; }
.main-contact { font-weight: 600; color: #1e293b; }
.sub-contact { font-size: 11px; color: #94a3b8; }
.email-text { color: #4f46e5; font-size: 13px; }

/* User Count Badge Style */
.users-interactive { cursor: pointer; transition: transform 0.2s; }
.users-interactive:hover { transform: scale(1.05); }
.variant-light-primary { background: #eef2ff; color: #4f46e5; border: 1px solid #e0e7ff; }

.user-count-badge {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;
  padding: 6px 10px;
  cursor: pointer;
}
.user-count-badge:hover { background: #eef2ff; color: #4f46e5; border-color: #4f46e5; }

/* Avatar & Info */
.customer-info { display: flex; align-items: center; gap: 12px; }
.avatar-mini {
  width: 34px; height: 34px;
  background: #f0f2ff; color: #4f46e5;
  border-radius: 8px; display: flex;
  align-items: center; justify-content: center;
  font-weight: 700; font-size: 13px;
  flex-shrink: 0;
}
.customer-tax { font-size: 11px; color: #94a3b8; margin-top: 2px; }

.supplier-avatar-large {
  width: 48px; height: 48px;
  background: #4f46e5; color: white;
  border-radius: 12px; display: flex;
  align-items: center; justify-content: center;
  font-size: 20px; font-weight: 700;
  flex-shrink: 0;
}

/* Card Body Typography */
.company-name { font-size: 16px; font-weight: 700; color: #1e293b; margin-bottom: 2px; }
.tax-badge { background: #f1f5f9; color: #475569; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
.supplier-details { margin-top: 15px; }
.detail-item { display: flex; align-items: center; color: #64748b; font-size: 12.5px; margin-bottom: 6px; gap: 10px; }
.detail-item i { width: 14px; color: #94a3b8; font-size: 12px; }

/* Actions */
.action-group { display: flex; gap: 8px; align-items: center; }
.circle-btn {
  width: 34px; height: 34px; border-radius: 50%;
  border: 1px solid #e5e9f2; background: white;
  display: flex; align-items: center; justify-content: center; transition: 0.2s;
  padding: 0;
}
.circle-btn.sm { width: 30px; height: 30px; }
.circle-btn:hover { background: #f8f9fb; border-color: #d1d5db; }

:deep(.delete-btn-wrapper .btn) {
  width: 34px; height: 34px; border-radius: 50%; padding: 0;
  display: flex; align-items: center; justify-content: center;
}
:deep(.delete-btn-wrapper.sm .btn) { width: 30px; height: 30px; }

.pagination-wrapper { display: flex; justify-content: center; }
.font-weight-600 { font-weight: 600; }
.color-dark { color: #1e293b; }

/* View Toggle Buttons */
.view-toggle-buttons {
  display: flex;
  gap: 8px;
}

.view-toggle-buttons .circle-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid #e5e9f2;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.view-toggle-buttons .circle-btn.active-view {
  background-color: #f0f2ff;
  border-color: #4f46e5;
}
.view-toggle-buttons .circle-btn:hover {
  background: #f8f9fb;
  border-color: #d1d5db;
  transform: translateY(-1px);
}


/* Grid View Styles */
.supplier-grid-view {
  display: grid;
  grid-template-columns: 1fr; /* Default: single column for small screens */
  gap: 20px;
  padding-top: 10px; /* Add some padding above the grid */
}

/* Media queries for larger screens */
@media (min-width: 576px) { /* Small devices (landscape phones, 576px and up) */
  .supplier-grid-view {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

@media (min-width: 768px) { /* Medium devices (tablets, 768px and up) */
  .supplier-grid-view {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (min-width: 992px) { /* Large devices (desktops, 992px and up) */
  .supplier-grid-view {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  }
}

@media (min-width: 1200px) { /* Extra large devices (large desktops, 1200px and up) */
  .supplier-grid-view {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }
}

.supplier-grid-view {
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
  padding-top: 0;
}

.supplier-profile-card {
  min-height: 82px;
  border-radius: 8px;
  border-color: #e0e6ef;
  padding: 14px 16px !important;
  overflow: hidden;
  position: relative;
  box-shadow: 0 8px 18px rgba(28, 39, 65, 0.05) !important;
  transition: transform 180ms cubic-bezier(0.23, 1, 0.32, 1), border-color 180ms ease, box-shadow 180ms ease;
}

.supplier-profile-card:hover {
  transform: translateY(-2px);
  border-color: #9b6cff;
  box-shadow: 0 12px 24px rgba(28, 39, 65, 0.08) !important;
}

.supplier-card-main {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
}

.supplier-card-copy {
  min-width: 0;
}

.supplier-card-main .company-name {
  font-size: 14px;
  line-height: 1.25;
  color: #14213d;
  margin-bottom: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.supplier-card-main .tax-badge {
  max-width: 100%;
  margin-top: 6px;
  background: #eef2f7;
  color: #43516d;
  padding: 4px 9px;
  border-radius: 5px;
  font-size: 11px;
  line-height: 1.2;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.supplier-card-main .supplier-avatar-large {
  border-radius: 8px;
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

.supplier-card-main .user-count-badge {
  background: #eef2ff;
  border-color: #dfe6ff;
  color: #4f46e5;
  box-shadow: 0 6px 14px rgba(79, 70, 229, 0.08);
}

.supplier-details {
  border-top: 1px solid #eef2f7;
  margin-top: 12px;
  padding-top: 10px;
}

.supplier-details .detail-item {
  margin-bottom: 5px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.supplier-card-actions {
  border-top: 1px solid #eef2f7;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
}

/* Responsive adjustments for table on small screens */
@media (max-width: 768px) {
  .invoice-dashboard-container {
    padding: 10px 0; /* Remove horizontal padding for very small screens */
  }
  .custom-card {
    border-radius: 0; /* Make card edges flush with screen */
  }
  /* For tables, responsive attribute handles most of it, but ensure text is readable */
  :deep(.b-table th), :deep(.b-table td) {
    padding: 10px !important;
    font-size: 12px;
  }
  .avatar-mini {
    width: 24px;
    height: 24px;
    font-size: 10px;
  }
  .customer-name {
    font-size: 12px;
  }
  .ref-link, .amount-bold {
    font-size: 13px;
  }
  .line-badge {
    padding: 3px 6px;
    font-size: 10px;
  }
  .supplier-profile-card {
    padding: 15px;
  }
  .supplier-avatar-large {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }
  .company-name {
    font-size: 14px;
  }
  .tax-badge, .detail-item {
    font-size: 11px;
  }
}
</style>
